// src/components/ProgressionDecisionPanel.js
//
// RULING 313 (Bob 2026-10-13: "The merits stay with the officers. The platform records the decision and judges nothing.
// yes" - "yes to all"). The union's decision not to progress a claim, under the SMART constitution Article 21B:
//   - the claim's Local Chairperson or General Chairperson records it, in his own name, with the reason (required);
//   - the server adds the dates: the written decision due in 30 days to the Local Chairperson and the Local's Secretary
//     (Section 44); the member's appeal due in 90 days (Section 75 I);
//   - the member is told the same day, with his options: the next appeal, or handling the claim himself (Section 44);
//   - the carrier's time limits keep running while he decides.
// The platform records the decision and judges nothing.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import RulingRef from './RulingRef';
import { isClaimOfficer } from '../data/carriage';
import { newDecision, appealPath, JUDGES_NOTHING, WRITTEN_DAYS, APPEAL_DAYS } from '../data/progressionDecision';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', text: '#DCE7F2' };
const ROLE = { local_chair: 'Local Chairperson', general_chair: 'General Chairperson' };

export default function ProgressionDecisionPanel({ claim, claimId, user }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Forensic audit 2026-10-16: a failed read used to become [] - the member saw no panel at all (as if nothing had been
  // decided) and the officer was offered a fresh decision that could duplicate one already on record.
  const [readErr, setReadErr] = useState('');
  const [loaded, setLoaded] = useState(false);
  const c = claim || {};
  const filed = !!c.status && c.status !== 'draft';
  const committee = c.committee || c.generalCommittee || '';
  const officer = !!user && isClaimOfficer({ uid: user.uid, role: user.role, committee: user.committee, scopes: user.scopes }, committee, c.scopeKey);

  useEffect(() => {
    setReadErr(''); setLoaded(false);
    if (!claimId || !filed) return undefined;
    return onSnapshot(collection(db, 'claims', claimId, 'progressionDecisions'),
      (s) => { setRows(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.decidedOn || '').localeCompare(String(a.decidedOn || '')))); setReadErr(''); setLoaded(true); },
      (e) => { setRows([]); setReadErr('Could not read the decisions on this claim: ' + ((e && (e.code || e.message)) || 'refused') + '. This is NOT "no decision" - whether one is on record is unknown until it can be read.'); setLoaded(true); });
  }, [claimId, filed]);

  if (!filed || (!officer && !rows.length && !readErr)) return null;

  const record = async () => {
    if (readErr || !loaded) return;   // Forensic audit 2026-10-16: never record against a record nobody has read
    setErr('');
    const r = newDecision({ claimId, claim: c, user, role: user.role, reason, serverTime: serverTimestamp() });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setBusy(true);
    try { await addDoc(collection(db, 'claims', claimId, 'progressionDecisions'), r.doc); setOpen(false); setReason(''); }
    catch (e) { setErr('Not recorded (' + (e.code || e.message) + ').'); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, borderRadius: 8, padding: 12, margin: '12px 0', color: C.text }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>
        Decision not to progress <RulingRef user={user} n={313} why="SMART constitution Article 21B, Sections 44 and 75: the decision in writing, and the member's appeal." />
      </div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>{JUDGES_NOTHING}</div>
      {readErr ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 8 }}>&#9888; {readErr}</div> : null}
      {rows.map(d => {
        const ap = appealPath(d.decidedRole);
        return (
          <div key={d.id} style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', fontSize: 13 }}>
            <div><strong>Not progressed</strong> - decided by {d.decidedByName || 'an officer'}, {ROLE[d.decidedRole] || d.decidedRole}{d.decidedOn ? ', on ' + d.decidedOn : ''}.</div>
            <div style={{ margin: '4px 0' }}>Reason: "{d.reason}"</div>
            {d.decidedOn ? (
              <>
                <div style={{ fontSize: 12, color: C.dim }}>Written decision due to the Local Chairperson and the Local's Secretary by <strong>{d.writtenDueOn}</strong> (Article 21B, Section 44).</div>
                {ap && <div style={{ fontSize: 12, marginTop: 4 }}>The member may appeal to {ap.to}, {ap.how}, by <strong>{d.appealDueOn}</strong> ({ap.section}) - or handle the claim himself by written notice to the union (Section 44).</div>}
                <div style={{ fontSize: 12, color: C.bad, marginTop: 4 }}>The carrier's time limits on this claim keep running while he decides.</div>
              </>
            ) : <div style={{ fontSize: 12, color: C.dim }}>Recording the dates...</div>}
          </div>
        );
      })}
      {/* Forensic audit 2026-10-16: no new decision is offered until the record has been read. */}
      {officer && !readErr && loaded && (!open ? (
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>Record a decision not to progress this claim</button>
      ) : (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>
            Recorded in your name as {ROLE[user.role] || user.role}. The member is told today, with the reason and his appeal date ({APPEAL_DAYS} days);
            the written decision is due to the Local Chairperson and the Local's Secretary in {WRITTEN_DAYS} days. It cannot be edited or deleted.
          </div>
          <textarea aria-label="The reason for the decision (required)" className="form-textarea" rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder="The reason for the decision (required)" style={{ width: '100%' }} />
          {err && <div style={{ color: C.bad, fontSize: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !reason.trim()} onClick={record}>Record the decision</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      ))}
    </div>
  );
}
